// TASK-001b: Inline fuzzy-search dropdown for create forms.
// Shows up to 5 existing records whose name fuzzy-matches the user's input.
// This is a *warning* component — it doesn't block submission, just informs.
import { Box, Typography, Paper, Stack } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useMemo } from 'react';

interface Props<T> {
  value: string;
  candidates: T[];
  /** Pull the comparable name string out of a candidate. */
  nameSelector: (c: T) => string;
  /** Optional secondary line shown next to each match. */
  secondarySelector?: (c: T) => string;
  /** Minimum characters before matching kicks in. Defaults to 2. */
  minLength?: number;
  /** Max matches shown. Defaults to 5. */
  limit?: number;
  /** Override label. */
  label?: string;
}

/** Case-insensitive partial match. Strips spaces, dots, dashes for fuzziness. */
function normalize(s: string): string {
  return s.toLowerCase().replace(/[\s._-]+/g, '');
}

export function DuplicateWarning<T>({
  value, candidates, nameSelector, secondarySelector,
  minLength = 2, limit = 5, label,
}: Props<T>) {
  const matches = useMemo(() => {
    const v = normalize(value || '');
    if (v.length < minLength) return [];
    return candidates
      .filter((c) => {
        const n = normalize(nameSelector(c) || '');
        return n.includes(v) || v.includes(n);
      })
      .slice(0, limit);
  }, [value, candidates, nameSelector, minLength, limit]);

  if (matches.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{
      px: 1.5, py: 1, bgcolor: 'warning.50',
      borderColor: 'warning.light', borderRadius: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Warning sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark' }}>
          {label || `Similar records found (${matches.length}):`}
        </Typography>
      </Box>
      <Stack spacing={0.25} sx={{ pl: 2.5 }}>
        {matches.map((c, i) => {
          const name = nameSelector(c);
          const secondary = secondarySelector?.(c);
          return (
            <Typography key={i} variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              • {name}{secondary ? ` — ${secondary}` : ''}
            </Typography>
          );
        })}
      </Stack>
    </Paper>
  );
}
